/**
 * Esquema de configuración de un tipo de actuador.
 *
 * Cada tipo declara en la base qué se le puede configurar a sus equipos
 * (`config_schema`), con qué valores viene de fábrica (`config_defaults`) y
 * cómo etiquetar cada campo (`ui_meta`). El formulario se dibuja a partir de
 * esto: dar de alta un tipo nuevo con otros campos no debe requerir tocar la
 * consola.
 *
 * Los datos llegan de la base tal como los escribió alguien a mano, así que
 * aquí se normaliza todo lo normalizable y se ignora lo que no se entienda:
 * un campo malformado no puede tumbar el formulario entero.
 */

export type TipoCampo = 'bool' | 'int' | 'number' | 'string' | 'enum' | 'enumlist';

export interface OpcionCampo {
  valor: string;
  etiqueta: string;
}

export interface CampoConfiguracion {
  clave: string;
  tipo: TipoCampo;
  etiqueta: string;
  ayuda: string | null;
  requerido: boolean;
  min: number | null;
  max: number | null;
  paso: number | null;
  unidad: string | null;
  /** Sólo para `enum` y `enumlist`. */
  opciones: OpcionCampo[];
}

const TIPOS_VALIDOS: readonly TipoCampo[] = ['bool', 'int', 'number', 'string', 'enum', 'enumlist'];

function comoObjeto(valor: unknown): Record<string, unknown> {
  // El esquema puede venir ya como objeto o como texto JSON, según la versión
  // con la que se guardó.
  if (typeof valor === 'string' && valor.trim()) {
    try {
      const interpretado: unknown = JSON.parse(valor);
      return typeof interpretado === 'object' && interpretado !== null
        ? (interpretado as Record<string, unknown>)
        : {};
    } catch {
      return {};
    }
  }

  return typeof valor === 'object' && valor !== null ? (valor as Record<string, unknown>) : {};
}

function comoNumero(valor: unknown): number | null {
  if (valor === null || valor === undefined || valor === '') return null;
  const n = Number(valor);
  return Number.isFinite(n) ? n : null;
}

function comoTexto(valor: unknown): string | null {
  return typeof valor === 'string' && valor.trim() ? valor.trim() : null;
}

/** Las opciones llegan como lista de textos o de `{value, label}`. */
function normalizarOpciones(campo: Record<string, unknown>): OpcionCampo[] {
  const cruda = campo.options ?? campo.items;
  if (!Array.isArray(cruda)) return [];

  const opciones: OpcionCampo[] = [];

  for (const elemento of cruda) {
    if (typeof elemento === 'string' || typeof elemento === 'number') {
      opciones.push({ valor: String(elemento), etiqueta: String(elemento) });
      continue;
    }

    if (typeof elemento === 'object' && elemento !== null) {
      const objeto = elemento as Record<string, unknown>;
      const valor = objeto.value ?? objeto.valor;
      if (valor === null || valor === undefined) continue;

      opciones.push({
        valor: String(valor),
        etiqueta: comoTexto(objeto.label) ?? String(valor),
      });
    }
  }

  return opciones;
}

/**
 * Interpreta el esquema crudo y lo deja listo para dibujar el formulario.
 *
 * `uiMeta` puede traer etiquetas y ayudas por campo que corrigen a las del
 * esquema; existe para renombrar campos sin tocar el esquema que valida el
 * servidor.
 */
export function interpretarEsquema(esquema: unknown, uiMeta?: unknown): CampoConfiguracion[] {
  const objeto = comoObjeto(esquema);
  const meta = comoObjeto(uiMeta);

  // El esquema puede ser `{fields: [...]}` o directamente la lista.
  const crudos = Array.isArray(objeto.fields)
    ? objeto.fields
    : Array.isArray(esquema)
      ? esquema
      : [];

  const campos: CampoConfiguracion[] = [];

  for (const crudo of crudos) {
    if (typeof crudo !== 'object' || crudo === null) continue;

    const campo = crudo as Record<string, unknown>;
    const clave = comoTexto(campo.key) ?? comoTexto(campo.name);
    if (!clave) continue;

    const tipoCrudo = (comoTexto(campo.type) ?? 'string').toLowerCase();
    const tipo = (TIPOS_VALIDOS as readonly string[]).includes(tipoCrudo)
      ? (tipoCrudo as TipoCampo)
      : 'string';

    const sobreescritura = comoObjeto(meta[clave]);

    campos.push({
      clave,
      tipo,
      etiqueta: comoTexto(sobreescritura.label) ?? comoTexto(campo.label) ?? clave,
      ayuda: comoTexto(sobreescritura.help) ?? comoTexto(campo.help),
      requerido: campo.required === true,
      min: comoNumero(campo.min),
      max: comoNumero(campo.max),
      paso: comoNumero(campo.step),
      unidad: comoTexto(campo.unit),
      opciones: normalizarOpciones(campo),
    });
  }

  return campos;
}

/** Valores de fábrica del tipo, como objeto plano. */
export function interpretarValoresBase(defaults: unknown): Record<string, unknown> {
  return comoObjeto(defaults);
}
